import React, { useMemo, useState, useCallback, useEffect } from "react";
import axios from "axios";
import Decrypt from "../../../../shared/utils/Decrypt";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/Store";
import DeliveryCenterAccordion from "./DeliveryCenterAccordion";
import MapDisplayForStores from "./MapDisplayForStores";
import VendorsForSelectedDeliveryCenter from "./VendorsForSelectedDeliveryCenter";

// ─────────────────────────────────────────────────────────────────────────────
// Unmapped Stores Panel
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const UnmappedStores = ({
  stores = [],
  selectedStores,
  onSelectionChange,
  isDragging,
  dragPayload,
  onDragStart,
  onDragEnd,
  onDropOnUnmapped,
  onDragOverUnmapped,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isHoveringDropZone, setIsHoveringDropZone] = useState(false);

  // Whether something is being dragged FROM a DC (can be dropped here to unmapped)
  const canReceiveDrop = isDragging && dragPayload?.fromDCId !== null;

  const filteredStores = useMemo(
    () =>
      stores.filter((store) => {
        const s = search.toLowerCase();
        return (
          store.details?.storeName?.toLowerCase().includes(s) ||
          store.details?.city?.toLowerCase().includes(s) ||
          store.details?.state?.toLowerCase().includes(s)
        );
      }),
    [stores, search],
  );

  const paginatedStores = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredStores.slice(start, start + PAGE_SIZE);
  }, [filteredStores, page]);

  const totalPages = Math.ceil(filteredStores.length / PAGE_SIZE);

  const handleSelectAll = () => {
    onSelectionChange(
      selectedStores.length === filteredStores.length
        ? []
        : [...filteredStores],
    );
  };

  const handleSelectRow = (store) => {
    // Reference identity — stores from Excel have no _id and can share names
    const isSelected = selectedStores.includes(store);
    onSelectionChange(
      isSelected
        ? selectedStores.filter((s) => s !== store)
        : [...selectedStores, store],
    );
  };

  const isRowSelected = (store) => selectedStores.includes(store);

  return (
    <Box
      // This whole panel is a drop zone for stores coming back from a DC
      onDragOver={(e) => {
        if (!canReceiveDrop) return;
        onDragOverUnmapped(e);
        setIsHoveringDropZone(true);
      }}
      onDragLeave={() => setIsHoveringDropZone(false)}
      onDrop={(e) => {
        setIsHoveringDropZone(false);
        onDropOnUnmapped(e);
      }}
      sx={{
        borderRadius: 2,
        border: canReceiveDrop
          ? isHoveringDropZone
            ? "2px dashed #C62828"
            : "2px dashed #EF9A9A"
          : "2px dashed transparent",
        transition: "border 0.15s, background 0.15s",
        bgcolor: isHoveringDropZone ? "#FFF5F5" : "transparent",
        minHeight: 300,
        p: 1,
      }}
    >
      {/* Header row */}
      {/* <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Tabs value={0}>
          <Tab label={`Unmapped Stores (${stores.length})`} />
        </Tabs>
        <TextField
          size="small"
          placeholder="Search Store..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box> */}

      {/* Drop hint shown while dragging a mapped store over this panel */}
      {canReceiveDrop && (
        <Box
          sx={{
            mb: 1,
            px: 2,
            py: 0.8,
            bgcolor: isHoveringDropZone ? "#FFEBEE" : "#FFF5F5",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" color="error" fontWeight={600}>
            ↙ Drop here to unassign from Delivery Center
          </Typography>
        </Box>
      )}

      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width={44}>
                <Checkbox
                  size="small"
                  checked={
                    selectedStores.length === filteredStores.length &&
                    filteredStores.length > 0
                  }
                  indeterminate={
                    selectedStores.length > 0 &&
                    selectedStores.length < filteredStores.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Address</TableCell>
              {/* <TableCell>Product</TableCell> */}
              {/* <TableCell align="center">Area</TableCell> */}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedStores.map((store, index) => {
              const selected = isRowSelected(store);
              return (
                <TableRow
                  hover
                  key={index}
                  draggable
                  onDragStart={(e) => onDragStart(e, store, null)}
                  onDragEnd={onDragEnd}
                  sx={{
                    cursor: "grab",
                    userSelect: "none",
                    bgcolor: selected ? "#EEF4FF" : undefined,
                    // Visual feedback: dim this row while it's being dragged
                    opacity:
                      dragPayload?.store === store &&
                      dragPayload?.fromDCId === null
                        ? 0.4
                        : 1,
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => handleSelectRow(store)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* Drag handle dots */}
                      <Box
                        sx={{
                          color: "text.disabled",
                          fontSize: 16,
                          lineHeight: 1,
                          letterSpacing: -2,
                          cursor: "grab",
                          flexShrink: 0,
                          mt: "2px",
                        }}
                        title="Drag to assign to a Delivery Center"
                      >
                        ⠿
                      </Box>
                      <Avatar
                        sx={{ bgcolor: "#FFF3E0", width: 32, height: 32 }}
                      >
                        <StoreIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {store.details?.storeName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {store.details?.storeOwnerName}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {store.details?.address}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {store.details?.city}, {store.details?.state}
                    </Typography>
                  </TableCell>

                  {/* <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {store.product?.productType}
                    </Typography>
                    <Typography variant="caption">
                      {store.product?.productWidth}" ×{" "}
                      {store.product?.productHeight}"
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      color="success"
                      size="small"
                      label={`${store.area} sqft`}
                    />
                  </TableCell> */}

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setSelectedStore(store);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedStores.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {stores.length === 0
                    ? "🎉 All stores have been assigned!"
                    : "No stores match your search."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        mt={2}
        alignItems="center"
      >
        <Typography variant="caption" color="text.secondary">
          {selectedStores.length > 0
            ? `${selectedStores.length} selected — use the banner above to assign`
            : "Drag a row to a Delivery Center, or select rows to bulk-assign"}
        </Typography>
        <Pagination
          size="small"
          page={page}
          count={totalPages}
          onChange={(_, value) => setPage(value)}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem>
          <LocationOnIcon sx={{ mr: 1 }} />
          View on Map
        </MenuItem>
      </Menu>
    </Box>
  );
};
export default UnmappedStores;
